import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { notify } from '../../common/functions/react-toastify.function';
import { handleApiError } from '../../common/functions/errorHandler.function';
import { uploadFiberPopExcelFile } from './functions/upload_fiber_pop_excel_file.function';

const FiberPopUploadModal = ({ open, onClose, onUploadComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    const validExtensions = ['.xls', '.xlsx'];

    setPreviewData(null);
    setSelectedFile(null);

    if (file && validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      setSelectedFile(file);
      await handleUpload(file);
    } else {
      notify('error', 'Please upload a valid Excel file (.xls or .xlsx).');
      setSelectedFile(null);
    }
  };

  const handleUpload = async (fileToUpload = selectedFile) => {
    if (!fileToUpload) {
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', fileToUpload);

    try {
      const response = await uploadFiberPopExcelFile(formData);
      if (response?.status === 200) {
        notify('success', 'File uploaded successfully');
        setPreviewData(response.response);
      } else {
        notify('error', response?.message || 'Upload failed');
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewData(null);
    onClose?.();
  };

  const handleOpenReview = () => {
    onUploadComplete?.(previewData);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Upload Fiber POP Excel</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            border: '2px dashed #ccc',
            borderRadius: '4px',
            padding: '20px',
            textAlign: 'center',
            cursor: 'pointer',
            position: 'relative',
            backgroundColor: '#f9f9f9',
          }}
          onClick={() => document.getElementById('fiberPopFileInput').click()}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              margin: '0 auto',
              backgroundColor: '#EBB9BB',
              borderRadius: '20%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8,
            }}
          >
            <AddIcon sx={{ fontSize: 32, color: '#BD1D23' }} />
          </Box>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Click or drag file to this area to upload
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Only Excel files (.xls, .xlsx) are supported.
          </Typography>

          <input
            id="fiberPopFileInput"
            type="file"
            accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </Box>

        {selectedFile && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid #ccc',
              background: 'rgba(0, 0, 0, 0.05)',
              borderRadius: '4px',
            }}
          >
            <Typography>{selectedFile.name}</Typography>
            {uploading && <CircularProgress size={18} />}
          </Box>
        )}

        {previewData && (
          <>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 20, color: '#F59E0B' }} />
              <Typography variant="body2">
                {previewData?.newSites?.length || 0} new sites found
              </Typography>
            </Box>
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 20, color: '#F59E0B' }} />
              <Typography variant="body2">
                {previewData?.updatedSites?.length || 0} sites with updated records
              </Typography>
            </Box>
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 20, color: '#F59E0B' }} />
              <Typography variant="body2">
                {previewData?.deletedSites?.length || 0} sites missing
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleOpenReview}
          variant="contained"
          sx={{
            backgroundColor: '#BD1D23',
            width: '92%',
            marginRight: '13px',
          }}
          disabled={!selectedFile || uploading}
        >
          Review
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiberPopUploadModal;
